import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
} from 'react';

export type NativeClickButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'type'
> & {
  /** Called on the button's NATIVE `click` event. */
  onActivate: () => void;
  /** The repo's Playwright attribute; React's DOM typings do not know it. */
  'test-id'?: string;
};

/**
 * A `<button type="button">` whose action is bound with `addEventListener`,
 * not with JSX `onClick`.
 *
 * Why: React 17+ dispatches its synthetic events from the root container, so a
 * JSX `onClick` only runs while the button sits inside that container. A
 * `ps-modal` moves its own host element into a `ps-portal-destination` under
 * `document.body` while it is open (`modal.wc.ts` `_openModal`), i.e. outside
 * the root — there the native click still fires and the JSX handler does not.
 * The design-system wrappers do not hit this because `@lit/react` binds the
 * events named in their `events` map natively (`Button` maps `onClick`; `Tag`
 * does not, which is why the pre-4343 `Tag` pills in this modal were dead too).
 *
 * Keyboard: a native `<button>` turns Enter and Space into a `click` itself, so
 * no `keydown` handler is added — one would run the action twice.
 *
 * The listener is attached once per mount and reads the CURRENT `onActivate`
 * through a ref, so a re-render with a new callback neither re-binds nor
 * leaves the old one behind. The button is rendered unconditionally, so the
 * ref is set by the time the effect runs. Kept free of design-system imports
 * so jsdom can render it (`native-click-button.test.tsx`).
 *
 * The same workaround already exists twice, each written inline for its own
 * element: `hooks/use-file-input.tsx` binds the hidden file input's `change`
 * natively, and `voice-channel-settings-form/languages-block/add-language-modal.tsx`
 * binds its Add button's `click` natively, reading the latest state by ref.
 * This is a third copy, on purpose: folding the three into one shared
 * primitive would change those two components, which is outside PSTUDIO-4343.
 */
export const NativeClickButton = ({
  onActivate,
  ...rest
}: NativeClickButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const onActivateRef = useRef(onActivate);

  useLayoutEffect(() => {
    onActivateRef.current = onActivate;
  });

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return undefined;
    const listener = () => onActivateRef.current();
    button.addEventListener('click', listener);
    return () => button.removeEventListener('click', listener);
  }, []);

  return <button {...rest} type="button" ref={buttonRef} />;
};
